import { toast, type ToastAction } from '../components/overlays/ToastContainer';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface ToastConfig {
	type?: ToastType;
	text1?: string;
	text2?: string;
	// Aliases for text1/text2 — many callers use title/message instead
	title?: string;
	message?: string;
	duration?: number;
	visibilityTime?: number;
	autoHide?: boolean;
	onPress?: () => void;
	onShow?: () => void;
	onHide?: () => void;
	// Optional action button rendered inside the toast (e.g. "Undo")
	action?: ToastAction;
}

class ToastAPI {
	private static instance: ToastAPI;

	private constructor() {}

	public static getInstance(): ToastAPI {
		if (!ToastAPI.instance) {
			ToastAPI.instance = new ToastAPI();
		}
		return ToastAPI.instance;
	}

	show(config: ToastConfig): void {
		const { type = 'info', duration, visibilityTime, action } = config;
		const resolvedText1 = config.text1 ?? config.title ?? '';
		const resolvedText2 = config.text2 ?? config.message;
		const message = resolvedText2
			? `${resolvedText1}\n${resolvedText2}`
			: resolvedText1;
		const finalDuration = duration || visibilityTime;

		// Call the appropriate toast method based on type
		switch (type) {
			case 'success':
				toast.success(message, finalDuration, action);
				break;
			case 'error':
				toast.error(message, finalDuration, action);
				break;
			case 'warning':
				toast.warning(message, finalDuration, action);
				break;
			case 'info':
			default:
				toast.info(message, finalDuration, action);
				break;
		}

		// Call onShow callback if provided
		config.onShow?.();
	}

	// Convenience methods for common toast types
	success(message: string, duration?: number): void {
		this.show({ type: 'success', text1: message, duration });
	}

	error(message: string, duration?: number): void {
		this.show({ type: 'error', text1: message, duration });
	}

	warning(message: string, duration?: number): void {
		this.show({ type: 'warning', text1: message, duration });
	}

	info(message: string, duration?: number): void {
		this.show({ type: 'info', text1: message, duration });
	}

	hide(): void {
		// Web implementation doesn't support hiding individual toasts
		// Warning: ToastAPI.hide() is not supported in web implementation
	}
}

// Export singleton instance
export const Toast = ToastAPI.getInstance();

// Also export as ToastAPI for compatibility
export { Toast as ToastAPI };
